import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../context/AuthContext";
import { controleProducaoService } from "../../../../services/controleProducaoService";
import { lojaService } from "../../../../services/lojaService";
import { Loja } from "../../../../types/loja";
import { UserStatsFicha, DailyStatsFicha } from "../types";
import { useRankingEvents } from "../../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../../constants/rankingEventQueryKeys";

interface RankingFichaDiariaContextData {
  usuarios: UserStatsFicha[];
  dailyStats: DailyStatsFicha;
  lojas: Loja[];
  isLoading: boolean;
  selectedLojaId?: number;
  showLojaSelector: boolean;
  dateLabel: string;
  selectedLojaNome?: string;
  criterio: string;
  toggleLojaSelector: () => void;
  selectAllLojas: () => void;
  selectLoja: (id: number) => void;
}

const RankingFichaDiariaContext = createContext<RankingFichaDiariaContextData>(
  {} as RankingFichaDiariaContextData
);

function splitNome(nomeCompleto: string): { nome: string; sobrenome: string } {
  const parts = nomeCompleto.trim().split(/\s+/);
  return {
    nome: parts[0] || "",
    sobrenome: parts.slice(1).join(" ") || "",
  };
}

function formatDataFicha(iso: string | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

export const RankingFichaDiariaProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );
  const [showLojaSelector, setShowLojaSelector] = useState(false);

  const { data: lojas = [], isLoading: isLoadingLojas } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const rankingFilters = useMemo(
    () => ({ lojaId: selectedLojaId ?? null }),
    [selectedLojaId]
  );

  const { data: rankingDiaData, isLoading: isLoadingRanking } = useQuery({
    queryKey: ["ranking-dia-ficha", rankingFilters],
    queryFn: () =>
      controleProducaoService.getRankingDiaFicha({
        lojaId: rankingFilters.lojaId ?? undefined,
      }),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });

  useRankingEvents("ficha-diaria", selectedLojaId, {
    queryKeys: RANKING_SSE_QUERY_KEYS.rankingFichaDiaria,
  });

  const isLoading = isLoadingLojas || isLoadingRanking;

  const usuarios: UserStatsFicha[] = useMemo(() => {
    if (!rankingDiaData?.ranking) return [];
    return rankingDiaData.ranking.map((item) => {
      const { nome, sobrenome } = splitNome(item.nome);
      return {
        usuarioId: item.usuarioId,
        nome,
        sobrenome,
        itensEncerrados: item.itensEncerrados,
        tempoMedioFormatado: item.tempoMedioFormatado,
        emProducao: item.emProducao,
        producaoKgFormatado: item.producaoKgFormatado,
        producaoPorUnd: item.producaoPorUnd ?? [],
        pontuacaoQtd: item.pontuacaoQtd,
        pontuacaoTempo: item.pontuacaoTempo,
        pontuacao: item.pontuacao,
      };
    });
  }, [rankingDiaData]);

  const dailyStats: DailyStatsFicha = useMemo(() => {
    if (!rankingDiaData?.resumo) {
      return {
        itensEncerrados: 0,
        emProducao: 0,
        tempoMedioFormatado: "00:00",
        usuarios: 0,
        producaoKgFormatado: "0",
        producaoPorUnd: [],
      };
    }
    return {
      itensEncerrados: rankingDiaData.resumo.itensEncerrados,
      emProducao: rankingDiaData.resumo.emProducao,
      tempoMedioFormatado: rankingDiaData.resumo.tempoMedioFormatado,
      usuarios: rankingDiaData.resumo.usuarios,
      producaoKgFormatado: rankingDiaData.resumo.producaoKgFormatado,
      producaoPorUnd: rankingDiaData.resumo.producaoPorUnd ?? [],
    };
  }, [rankingDiaData]);

  const dateLabel = formatDataFicha(rankingDiaData?.dataFicha);
  const criterio =
    rankingDiaData?.criterio ||
    "Nota de 0 a 10. O melhor do dia vale 10. Produzir mais e mais rápido sobe a nota.";

  const selectedLojaNome = useMemo(
    () =>
      rankingDiaData?.lojaNome ||
      lojas.find((l) => l.id === selectedLojaId)?.nome,
    [lojas, selectedLojaId, rankingDiaData?.lojaNome]
  );

  const toggleLojaSelector = useCallback(() => {
    setShowLojaSelector((prev) => !prev);
  }, []);

  const selectAllLojas = useCallback(() => {
    setSelectedLojaId(undefined);
    setShowLojaSelector(false);
  }, []);

  const selectLoja = useCallback((id: number) => {
    setSelectedLojaId(id);
    setShowLojaSelector(false);
  }, []);

  return (
    <RankingFichaDiariaContext.Provider
      value={{
        usuarios,
        dailyStats,
        lojas,
        isLoading,
        selectedLojaId,
        showLojaSelector,
        dateLabel,
        selectedLojaNome,
        criterio,
        toggleLojaSelector,
        selectAllLojas,
        selectLoja,
      }}
    >
      {children}
    </RankingFichaDiariaContext.Provider>
  );
};

export const useRankingFichaDiaria = (): RankingFichaDiariaContextData => {
  const context = useContext(RankingFichaDiariaContext);
  if (!context) {
    throw new Error(
      "useRankingFichaDiaria must be used within RankingFichaDiariaProvider"
    );
  }
  return context;
};
